import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // Si la ventana cambia de tamaño (o el celular rota) con el panel
    // abierto, se recalcula la altura de 3 filas de la cuadricula de
    // productos para que no quede desajustada.
    window.addEventListener('resize', this.#handleWindowResize, { signal });

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Recalcula el limite de 3 filas de la cuadricula de productos cuando
   * cambia el tamaño de la ventana.
   */
  #handleWindowResize = debounce(() => {
    this.#capProductGridRows();
  }, 150);

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        // Nunca se deja que Enter dispare el envio nativo del formulario (eso es
        // lo que causaba la redireccion a una URL como
        // /search?q=...&options[prefix]=last): siempre se previene el
        // comportamiento por defecto primero. Los resultados siempre se quedan
        // dentro del panel; la unica navegacion permitida es a un resultado
        // concreto que el usuario ya selecciono con las flechas (equivalente a
        // hacer clic en esa tarjeta) o al unico resultado cuando la busqueda
        // encuentra exactamente una coincidencia.
        event.preventDefault();

        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          this.#currentItem?.querySelector('a')?.click();
        }
        // Si no hay ningun resultado seleccionado con las flechas, no se hace
        // nada mas: los resultados ya se muestran en vivo dentro del panel
        // mientras se escribe, y no hay ninguna pagina de resultados a la
        // cual redirigir.
        break;
      }
    }
  };

  /**
   * Red de seguridad adicional: el <form> del panel de busqueda apunta a
   * routes.search_url (para que funcione sin JavaScript), pero dentro del
   * panel NUNCA queremos que se envie de verdad — eso navegaria fuera del
   * panel a una URL como /search?q=...&options[prefix]=last. Se anula
   * siempre, sin importar que lo haya disparado (Enter, un boton
   * type="submit", etc.).
   * @param {SubmitEvent} event - The submit event.
   */
  handleFormSubmit = (event) => {
    event.preventDefault();
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   *
   * La busqueda predictiva nativa de Shopify (predictive_search_url) tiene un
   * limite duro de 10 resultados por tipo de recurso — eso no se puede subir.
   * Para poder mostrar TODOS los productos que coincidan (no solo 10), se hace
   * una segunda peticion en paralelo al buscador normal de Shopify
   * (search_url) filtrada solo a productos, que sí permite hasta 50 por
   * pagina. Los resultados que no son productos (sugerencias de busqueda,
   * colecciones, paginas, articulos) siguen viniendo unicamente de la
   * busqueda predictiva nativa, sin cambios. Ninguna de las dos peticiones
   * navega a ninguna parte: ambas devuelven solo el HTML de la seccion, que
   * se combina y se pinta dentro del propio panel.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const predictiveUrl = new URL(Theme.routes.predictive_search_url, location.origin);
    predictiveUrl.searchParams.set('q', searchTerm);
    predictiveUrl.searchParams.set('resources[limit_scope]', 'each');

    const fullProductsUrl = new URL(Theme.routes.search_url, location.origin);
    fullProductsUrl.searchParams.set('q', searchTerm);
    // El buscador normal de Shopify (search_url) filtra por tipo de recurso
    // con el parametro "type" (no "resources[type]", que es solo para el
    // endpoint de busqueda predictiva). Con "resources[type]" el buscador
    // normal lo ignora y devuelve una mezcla de productos, paginas,
    // articulos y colecciones, lo que rompe la plantilla (que espera solo
    // productos) y hace que la peticion falle en silencio.
    fullProductsUrl.searchParams.set('type', 'product');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    Promise.all([
      sectionRenderer.getSectionHTML(this.dataset.sectionId, false, predictiveUrl),
      sectionRenderer.getSectionHTML(this.dataset.sectionId, false, fullProductsUrl).catch((error) => {
        // Si esta segunda peticion falla, se sigue mostrando el panel con lo
        // que trajo la busqueda predictiva nativa (como antes), en vez de
        // romper todo el panel. Se deja un aviso en la consola para poder
        // diagnosticarlo si vuelve a pasar.
        console.error('No se pudo traer la lista completa de productos:', error);
        return null;
      }),
    ])
      .then(([predictiveMarkup, fullProductsMarkup]) => {
        if (!predictiveMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        const resultsMarkup = this.#mergeFullProductList(predictiveMarkup, fullProductsMarkup);

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
        this.#capProductGridRows();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Reemplaza la lista de productos (maximo ~10, la que trae la busqueda
   * predictiva nativa) por la lista completa de productos (hasta 50) que
   * trae la busqueda normal filtrada a productos. El resto del markup
   * (sugerencias, colecciones, paginas, articulos) se deja tal cual vino de
   * la busqueda predictiva.
   * @param {string} predictiveMarkup - HTML de la seccion via predictive_search_url.
   * @param {string | null} fullProductsMarkup - HTML de la seccion via search_url (solo productos).
   * @returns {string} El HTML final a pintar en el panel.
   */
  #mergeFullProductList(predictiveMarkup, fullProductsMarkup) {
    if (!fullProductsMarkup) return predictiveMarkup;

    const parser = new DOMParser();
    const predictiveDoc = parser.parseFromString(predictiveMarkup, 'text/html');
    const fullProductsDoc = parser.parseFromString(fullProductsMarkup, 'text/html');

    const fullProductsEl = fullProductsDoc.getElementById('predictive-search-products');
    if (!fullProductsEl) return predictiveMarkup;

    const currentProductsEl = predictiveDoc.getElementById('predictive-search-products');
    if (currentProductsEl) {
      currentProductsEl.replaceWith(fullProductsEl);
    } else {
      const resultsInner = predictiveDoc.querySelector('[data-search-results]');
      if (!resultsInner) return predictiveMarkup;

      const queriesList = resultsInner.querySelector('.predictive-search-results__wrapper-queries');
      if (queriesList) {
        queriesList.after(fullProductsEl);
      } else {
        resultsInner.prepend(fullProductsEl);
      }
    }

    const root = predictiveDoc.getElementById('predictive-search-results');
    return root ? root.outerHTML : predictiveMarkup;
  }

  /**
   * Limita la cuadricula de productos a un maximo de 3 filas visibles,
   * dejando el resto accesible con scroll dentro del propio panel. La altura
   * de fila se mide en vivo (no un valor fijo) para que funcione igual sin
   * importar el ancho de pantalla (2 o 4 columnas) o el tamaño de letra.
   */
  #capProductGridRows() {
    const grid = this.querySelector('.predictive-search-results__wrapper-products');
    if (!(grid instanceof HTMLElement)) return;

    const items = Array.from(grid.children).filter((child) => child instanceof HTMLElement);
    if (items.length === 0) return;

    const columns = new Set(items.map((item) => Math.round(item.offsetLeft))).size || 1;

    if (items.length <= columns * 3) {
      grid.style.removeProperty('max-height');
      grid.style.removeProperty('overflow-y');
      return;
    }

    const firstRowItem = items[0];
    const secondRowItem = items[columns];
    if (!(secondRowItem instanceof HTMLElement)) return;

    // rowStep = altura de una fila + el gap hasta la siguiente. Con eso y la
    // altura real de una tarjeta se calcula la altura exacta de 3 filas
    // completas (2 filas + gap de por medio, mas la altura de la 3ra fila).
    const rowStep = secondRowItem.offsetTop - firstRowItem.offsetTop;
    const threeRowsHeight = rowStep > 0 ? rowStep * 2 + firstRowItem.offsetHeight : null;

    if (threeRowsHeight) {
      grid.style.maxHeight = `${threeRowsHeight}px`;
      grid.style.overflowY = 'auto';
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
